import React, { useState, useEffect, useRef } from "react";
import { Drawer } from "antd";
import {
  DownOutlined, MenuOutlined, CloseOutlined,
  FilePdfOutlined, FileWordOutlined, FileExcelOutlined,
  FilePptOutlined, FileImageOutlined, QrcodeOutlined,
  LinkOutlined, ReadOutlined, MergeCellsOutlined,
  ScissorOutlined, SortAscendingOutlined, CompressOutlined,
  FontSizeOutlined, EditOutlined, LockOutlined, UnlockOutlined,
  AppstoreOutlined, LoginOutlined, EnvironmentOutlined, FileOutlined,
  IdcardOutlined, MailOutlined, PictureOutlined, TagOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { getAdvancedToolRoute } from "../../utils/navigation";
import "./Header.css";

const CONVERT_ITEMS = [
  { icon: <FileWordOutlined />,  label: "Word to PDF",       path: "/word2pdf",  desc: "Convert .DOC & .DOCX", color: "#2b5ce6" },
  { icon: <FileExcelOutlined />, label: "Excel to PDF",      path: "/excel2pdf", desc: "Convert .XLS & .XLSX", color: "#1a7a43" },
  { icon: <FilePptOutlined />,   label: "PowerPoint to PDF", path: "/ppt2pdf",   desc: "Convert .PPT & .PPTX", color: "#c84b1e" },
  { icon: <FileImageOutlined />, label: "Image to PDF",      path: "/image2pdf", desc: "JPG, PNG, WEBP & more", color: "#7b3fe4" },
];

const QR_ITEMS = [
  { icon: <LinkOutlined />, label: "Website QR", path: "/qr-link", desc: "Website URL", color: "#0891b2" },
  { icon: <QrcodeOutlined />, label: "Text QR", path: "/qr-text", desc: "Rich text content", color: "#6366f1" },
  { icon: <EnvironmentOutlined />, label: "Location QR", path: "/qr-location", desc: "Maps or venue", color: "#1a7a43" },
  { icon: <PictureOutlined />, label: "Brand QR", path: "/qr-brand", desc: "Website with logo", color: "#e5322d" },
  { icon: <AppstoreOutlined />, label: "Social QR", path: "/qr-social", desc: "Social profile", color: "#7b3fe4" },
  { icon: <FileOutlined />, label: "File QR", path: "/qr-file", desc: "Upload a file", color: "#c84b1e" },
  { icon: <IdcardOutlined />, label: "vCard QR", path: "/qr-vcard", desc: "Contact card", color: "#2b5ce6" },
  { icon: <TagOutlined />, label: "Product QR", path: "/qr-product", desc: "Product link", color: "#d97706" },
  { icon: <MailOutlined />, label: "Message QR", path: "/qr-message", desc: "Greeting or invite", color: "#be185d" },
];
const QR_PATHS = QR_ITEMS.map((item) => item.path);

// All PDF tools — grouped for the mega-menu
const ALL_PDF_GROUPS = [
  {
    label: "Convert to PDF",
    eyebrow: "Free · No login needed",
    free: true,
    items: CONVERT_ITEMS,
  },
  {
    label: "Organize & Edit",
    eyebrow: "Login required",
    free: false,
    items: [
      { icon: <MergeCellsOutlined />,    label: "Merge PDF",       desc: "Join 2+ PDF files",           color: "#6366f1" },
      { icon: <ScissorOutlined />,       label: "Split PDF",       desc: "Extract pages from a PDF",    color: "#0891b2" },
      { icon: <SortAscendingOutlined />, label: "Rearrange PDF",   desc: "Reorder pages easily",        color: "#0891b2" },
      { icon: <CompressOutlined />,      label: "Compress PDF",    desc: "Reduce PDF file size",        color: "#1a7a43" },
    ],
  },
  {
    label: "Security & More",
    eyebrow: "Login required",
    free: false,
    items: [
      { icon: <FontSizeOutlined />, label: "OCR PDF",          desc: "Extract text from any PDF",       color: "#7b3fe4" },
      { icon: <EditOutlined />,     label: "E-sign PDF",       desc: "Add your signature to PDFs",      color: "#c84b1e" },
      { icon: <LockOutlined />,     label: "Add Password",     desc: "Protect PDF with a password",     color: "#e5322d" },
      { icon: <UnlockOutlined />,   label: "Remove Password",  desc: "Unlock password-protected PDFs",  color: "#e5322d" },
    ],
  },
];

const HeaderLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = !!localStorage.getItem("token");

  const [scrolled,       setScrolled]       = useState(false);
  const [drawerOpen,     setDrawerOpen]     = useState(false);
  const [convertOpen,    setConvertOpen]    = useState(false);
  const [qrOpen,         setQrOpen]         = useState(false);
  const [allPdfOpen,     setAllPdfOpen]     = useState(false);
  const [mobConvertOpen, setMobConvertOpen] = useState(false);
  const [mobQrOpen,      setMobQrOpen]      = useState(false);
  const [mobAllPdfOpen,  setMobAllPdfOpen]  = useState(false);

  const convertRef = useRef(null);
  const qrRef      = useRef(null);
  const allPdfRef  = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (convertRef.current && !convertRef.current.contains(e.target)) setConvertOpen(false);
      if (qrRef.current      && !qrRef.current.contains(e.target))      setQrOpen(false);
      if (allPdfRef.current  && !allPdfRef.current.contains(e.target))  setAllPdfOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const isActive = (paths) =>
    paths.some((p) => location.pathname === p || location.pathname.startsWith(p));

  const go = (path) => {
    navigate(path);
    setConvertOpen(false); setQrOpen(false); setAllPdfOpen(false); setDrawerOpen(false);
  };

  const goAdvanced = (label) => {
    setConvertOpen(false); setQrOpen(false); setAllPdfOpen(false); setDrawerOpen(false);
    navigate(
      isAuthenticated ? getAdvancedToolRoute(label) : "/login",
      isAuthenticated ? undefined : { state: { toolName: label } }
    );
  };

  const handlePrimaryAuthAction = () => {
    navigate(isAuthenticated ? "/dashboard/home" : "/register");
  };

  const handleSecondaryAuthAction = () => {
    if (isAuthenticated) {
      localStorage.clear();
      navigate("/");
      return;
    }

    navigate("/login");
  };

  return (
    <>
      <header className={`hdr ${scrolled ? "hdr-scrolled" : ""}`}>
        <div className="hdr-inner">

          {/* Logo */}
          <div className="hdr-logo" onClick={() => navigate("/")}>
            <img src={require("../../assets/logo.png")} alt="Bonoxi" />
          </div>

          <nav className="hdr-nav">

            {/* ── Convert PDF ── */}
            <div
              className={`hdr-nav-item hdr-dropdown-wrap ${isActive(["/word2pdf","/excel2pdf","/ppt2pdf","/image2pdf"]) ? "active" : ""}`}
              ref={convertRef}
              onMouseEnter={() => setConvertOpen(true)}
              onMouseLeave={() => setConvertOpen(false)}
            >
              <button className="hdr-nav-btn" onClick={() => setConvertOpen(!convertOpen)}>
                <FilePdfOutlined className="hdr-nav-icon" />
                Convert PDF
                <DownOutlined className={`hdr-chevron ${convertOpen ? "open" : ""}`} />
              </button>
              {convertOpen && (
                <div className="hdr-dropdown">
                  <div className="hdr-dropdown-label">
                    PDF Converters
                    <span className="hdr-free-badge">Free</span>
                  </div>
                  {CONVERT_ITEMS.map((item) => (
                    <button key={item.path} className="hdr-dropdown-item" onClick={() => go(item.path)}>
                      <span className="hdr-drop-icon" style={{ background: item.color + "12", color: item.color }}>{item.icon}</span>
                      <span className="hdr-drop-text">
                        <span className="hdr-drop-label">{item.label}</span>
                        <span className="hdr-drop-desc">{item.desc}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ── Generate QR ── */}
            <div
              className={`hdr-nav-item hdr-dropdown-wrap ${isActive(QR_PATHS) ? "active" : ""}`}
              ref={qrRef}
              onMouseEnter={() => setQrOpen(true)}
              onMouseLeave={() => setQrOpen(false)}
            >
              <button className="hdr-nav-btn" onClick={() => setQrOpen(!qrOpen)}>
                <QrcodeOutlined className="hdr-nav-icon" />
                Generate QR
                <DownOutlined className={`hdr-chevron ${qrOpen ? "open" : ""}`} />
              </button>
              {qrOpen && (
                <div className="hdr-dropdown hdr-qr-dropdown">
                  <div className="hdr-dropdown-label">QR Tools</div>
                  {QR_ITEMS.map((item) => (
                    <button key={item.path} className="hdr-dropdown-item" onClick={() => go(item.path)}>
                      <span className="hdr-drop-icon" style={{ background: item.color + "12", color: item.color }}>{item.icon}</span>
                      <span className="hdr-drop-text">
                        <span className="hdr-drop-label">{item.label}</span>
                        <span className="hdr-drop-desc">{item.desc}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ── Blog ── */}
            <div className={`hdr-nav-item ${isActive(["/blogs","/blog"]) ? "active" : ""}`}>
              <button className="hdr-nav-btn" onClick={() => navigate("/blogs")}>
                <ReadOutlined className="hdr-nav-icon" />
                Blog
              </button>
            </div>

            {/* ── All PDF Tools (mega-menu) ── */}
            <div
              className="hdr-nav-item hdr-dropdown-wrap"
              ref={allPdfRef}
              onMouseEnter={() => setAllPdfOpen(true)}
              onMouseLeave={() => setAllPdfOpen(false)}
            >
              <button className={`hdr-nav-btn hdr-all-tools-btn`} onClick={() => setAllPdfOpen(!allPdfOpen)}>
                <AppstoreOutlined className="hdr-nav-icon" />
                All PDF Tools
                <DownOutlined className={`hdr-chevron ${allPdfOpen ? "open" : ""}`} />
              </button>

              {allPdfOpen && (
                <div className="hdr-mega">
                  {ALL_PDF_GROUPS.map((group, gi) => (
                    <React.Fragment key={group.label}>
                      {gi > 0 && <div className="hdr-mega-divider" />}
                      <div className="hdr-mega-col">
                        <div className="hdr-mega-col-label">
                          {group.label}
                          {group.free
                            ? <span className="hdr-free-badge">Free</span>
                            : <span className="hdr-login-badge"><LoginOutlined /> Login required</span>
                          }
                        </div>
                        <div className="hdr-mega-eyebrow">{group.eyebrow}</div>
                        {group.items.map((item) => (
                          <button
                            key={item.label}
                            className="hdr-dropdown-item"
                            onClick={() => item.path ? go(item.path) : goAdvanced(item.label)}
                          >
                            <span className="hdr-drop-icon" style={{ background: item.color + "12", color: item.color }}>{item.icon}</span>
                            <span className="hdr-drop-text">
                              <span className="hdr-drop-label">{item.label}</span>
                              <span className="hdr-drop-desc">{item.desc}</span>
                            </span>
                          </button>
                        ))}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>

          </nav>

          {/* Auth */}
          <div className="hdr-auth">
            <button className="hdr-login-btn" onClick={handleSecondaryAuthAction}>
              {isAuthenticated ? "Log out" : "Log in"}
            </button>
            <button className="hdr-signup-btn" onClick={handlePrimaryAuthAction}>
              {isAuthenticated ? "Go to dashboard" : "Sign up free"}
            </button>
          </div>

          <button className="hdr-hamburger" onClick={() => setDrawerOpen(true)}>
            <MenuOutlined />
          </button>
        </div>
      </header>

      {/* ── Mobile Drawer ── */}
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        placement="right"
        width={300}
        closeIcon={<CloseOutlined />}
        title={<img src={require("../../assets/logo.png")} alt="Bonoxi" style={{ height: 28 }} />}
        styles={{ body: { padding: "16px 0" } }}
      >
        <div className="mob-nav">

          {/* Convert PDF */}
          <div className="mob-section">
            <button className="mob-section-toggle" onClick={() => setMobConvertOpen(!mobConvertOpen)}>
              <span><FilePdfOutlined /> Convert PDF</span>
              <DownOutlined className={`mob-chevron ${mobConvertOpen ? "open" : ""}`} />
            </button>
            {mobConvertOpen && (
              <div className="mob-sub">
                {CONVERT_ITEMS.map((item) => (
                  <button key={item.path} className="mob-sub-item" onClick={() => go(item.path)}>
                    <span style={{ color: item.color }}>{item.icon}</span> {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Generate QR */}
          <div className="mob-section">
            <button className="mob-section-toggle" onClick={() => setMobQrOpen(!mobQrOpen)}>
              <span><QrcodeOutlined /> Generate QR</span>
              <DownOutlined className={`mob-chevron ${mobQrOpen ? "open" : ""}`} />
            </button>
            {mobQrOpen && (
              <div className="mob-sub">
                {QR_ITEMS.map((item) => (
                  <button key={item.path} className="mob-sub-item" onClick={() => go(item.path)}>
                    <span style={{ color: item.color }}>{item.icon}</span> {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Blog */}
          <div className="mob-section">
            <button className="mob-section-toggle" onClick={() => go("/blogs")}>
              <span><ReadOutlined /> Blog</span>
            </button>
          </div>

          {/* All PDF Tools */}
          <div className="mob-section">
            <button className="mob-section-toggle mob-all-tools-toggle" onClick={() => setMobAllPdfOpen(!mobAllPdfOpen)}>
              <span><AppstoreOutlined /> All PDF Tools</span>
              <DownOutlined className={`mob-chevron ${mobAllPdfOpen ? "open" : ""}`} />
            </button>
            {mobAllPdfOpen && (
              <div className="mob-sub">
                {ALL_PDF_GROUPS.map((group) => (
                  <div key={group.label}>
                    <div className="mob-group-label">
                      {group.label}
                      {!group.free && <span className="mob-login-pill"><LoginOutlined /> Login</span>}
                    </div>
                    {group.items.map((item) => (
                      <button
                        key={item.label}
                        className="mob-sub-item"
                        onClick={() => item.path ? go(item.path) : goAdvanced(item.label)}
                      >
                        <span style={{ color: item.color }}>{item.icon}</span> {item.label}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mob-auth">
            <button className="mob-login-btn" onClick={handleSecondaryAuthAction}>
              {isAuthenticated ? "Log out" : "Log in"}
            </button>
            <button className="mob-signup-btn" onClick={handlePrimaryAuthAction}>
              {isAuthenticated ? "Go to dashboard" : "Sign up free"}
            </button>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default HeaderLayout;
